import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { Accordion, Box, VStack } from '@chakra-ui/react'
import { OrgFeaturesPulse } from './OrgFeatures/OrgFeaturesPulse'
import { OrgFeaturesSso } from './OrgFeatures/OrgFeaturesSso'

/**
 * The org's features, stacked and separated by a single muted rule — no card
 * chrome around either, since the Features card they sit in already provides
 * it.
 *
 * The two are shaped differently on purpose. Pulse is read-only detail, so it
 * collapses in place. SSO is a form wide enough to need its own surface, so
 * its row is a summary with a Configure button and the editing happens in a
 * drawer — which is also why no discard plumbing lives here any more: the
 * drawer owns the edits, so it owns the guard on throwing them away.
 */
export function FeaturesSections() {
  // RBAC permissions
  const { hasAdminRights, hasOperatorRights, hasReaderRights } =
    useRBACPermissions()

  // Permission logic:
  // - No rights: sections render but cannot be opened (canRead = false)
  // - Reader rights only: view only, cannot edit (canRead = true, canEdit = false)
  // - Operator/Admin rights: can read and edit features (canRead = true, canEdit = true)
  const canRead = hasAdminRights() || hasOperatorRights() || hasReaderRights()
  const canEdit = hasAdminRights() || hasOperatorRights()

  return (
    <VStack align="stretch" gap={0} width="100%">
      <Box borderBottomWidth="1px" borderColor="border.muted">
        <OrgFeaturesSso canRead={canRead} canEdit={canEdit} />
      </Box>
      <Accordion.Root
        variant="outline"
        multiple
        collapsible
        borderColor="border.muted"
        // `outline` rules every item off at the bottom, which would leave a
        // stray line under the last section with nothing below it to divide.
        css={{ '& > *:last-of-type': { borderBottomWidth: 0 } }}
      >
        <OrgFeaturesPulse canRead={canRead} />
      </Accordion.Root>
    </VStack>
  )
}
